import '../monaco-setup';
import Editor, { type OnMount } from '@monaco-editor/react';
import { useRef, useState } from 'react';
import { applyTransform, type EncodeKind } from '@xsscool/shared';
import { useStore } from '../store';
import { displayUrl } from '../api';
import { createCapture, listCaptures } from '../api-captures';
import { SharesMenu } from './SharesMenu';

type MonacoEditor = Parameters<OnMount>[0];

interface ActionDef {
  id: string;
  label: string;
  direction: 'encode' | 'decode';
  kind: EncodeKind;
  urlMode?: 'non-ascii' | 'all';
}

const ACTIONS: ActionDef[] = [
  { id: 'encode-url-non-ascii', label: 'Encode: URL (non-ASCII)', direction: 'encode', kind: 'url', urlMode: 'non-ascii' },
  { id: 'encode-url-all', label: 'Encode: URL (every byte)', direction: 'encode', kind: 'url', urlMode: 'all' },
  { id: 'encode-html', label: 'Encode: HTML entities', direction: 'encode', kind: 'html' },
  { id: 'encode-js', label: 'Encode: JS string', direction: 'encode', kind: 'js' },
  { id: 'encode-base64', label: 'Encode: Base64', direction: 'encode', kind: 'base64' },
  { id: 'decode-url', label: 'Decode: URL', direction: 'decode', kind: 'url' },
  { id: 'decode-html', label: 'Decode: HTML entities', direction: 'decode', kind: 'html' },
  { id: 'decode-js', label: 'Decode: JS string', direction: 'decode', kind: 'js' },
  { id: 'decode-base64', label: 'Decode: Base64', direction: 'decode', kind: 'base64' },
];

function transformSelections(editor: MonacoEditor, action: ActionDef): void {
  const model = editor.getModel();
  const selections = editor.getSelections();
  if (!model || !selections || selections.length === 0) return;

  const edits = [];
  for (const sel of selections) {
    if (sel.isEmpty()) continue;
    const original = model.getValueInRange(sel);
    const transformed = applyTransform({
      direction: action.direction,
      kind: action.kind,
      value: original,
      urlOpts: action.urlMode ? { mode: action.urlMode } : undefined,
    });
    if (transformed === original) continue;
    edits.push({ range: sel, text: transformed, forceMoveMarkers: true });
  }
  if (edits.length === 0) return;
  editor.pushUndoStop();
  editor.executeEdits('xsscool-transform', edits);
  editor.pushUndoStop();
}

function registerActions(editor: MonacoEditor): void {
  let order = 1;
  for (const action of ACTIONS) {
    editor.addAction({
      id: `xsscool.${action.id}`,
      label: action.label,
      contextMenuGroupId: '1_modification',
      contextMenuOrder: order++,
      run: (ed) => transformSelections(ed as MonacoEditor, action),
    });
  }
}

export function BodyEditor() {
  const editorLanguage = useStore((s) => s.editorLanguage);
  const editorContent = useStore((s) => s.editorContent);
  const setEditorContent = useStore((s) => s.setEditorContent);
  const domainBase = useStore((s) => s.domainBase);
  const domainSub = useStore((s) => s.domainSub);
  const editorRef = useRef<MonacoEditor | null>(null);
  const [insertingCapture, setInsertingCapture] = useState(false);
  const [captureNote, setCaptureNote] = useState<string | null>(null);
  const [snippetMenuOpen, setSnippetMenuOpen] = useState(false);

  const insertAtCursor = (text: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = editor.getSelection();
    if (!selection) return;
    editor.executeEdits('xsscool-insert-text', [
      { range: selection, text, forceMoveMarkers: true },
    ]);
    editor.focus();
  };


  const handleMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    registerActions(editor);
    // App shortcuts must work while the editor has focus. Monaco stops
    // propagation for keys it knows (⌘K is a chord prefix), so register them
    // as editor commands too.
    const { KeyMod, KeyCode } = monaco;
    const st = () => useStore.getState();
    const save = (forceNew: boolean) =>
      window.dispatchEvent(new CustomEvent('xsscool:save', { detail: { forceNew } }));
    editor.addCommand(KeyMod.CtrlCmd | KeyCode.KeyS, () => save(false));
    editor.addCommand(KeyMod.CtrlCmd | KeyMod.Shift | KeyCode.KeyS, () => save(true));
    editor.addCommand(KeyMod.CtrlCmd | KeyCode.KeyK, () => st().setPaletteOpen(!st().paletteOpen));
    editor.addCommand(KeyMod.CtrlCmd | KeyCode.KeyB, () => st().setPayloadsOpen(!st().payloadsOpen));
    editor.addCommand(KeyMod.CtrlCmd | KeyCode.Digit1, () => st().setTab('body'));
    editor.addCommand(KeyMod.CtrlCmd | KeyCode.Digit2, () => st().setTab('headers'));
    editor.addCommand(KeyMod.CtrlCmd | KeyCode.Digit3, () => st().setTab('captures'));
  };

  const insertCaptureUrl = async () => {
    const editor = editorRef.current;
    if (!editor || insertingCapture) return;
    setInsertingCapture(true);
    setCaptureNote(null);
    try {
      const existing = await listCaptures();
      let captureId: string;
      let created = false;
      if (existing.length > 0) {
        captureId = existing[0]!.id;
      } else {
        const fresh = await createCapture();
        captureId = fresh.id;
        created = true;
        // Surface the new capture to other listeners (CapturesPanel et al).
        window.dispatchEvent(new Event('xsscool:captures-changed'));
      }
      insertAtCursor(displayUrl(`/c/${captureId}`, { domainBase, domainSub }));
      setCaptureNote(created ? 'Created new capture' : 'Used existing capture');
      window.setTimeout(() => setCaptureNote(null), 2000);
    } catch (e) {
      setCaptureNote(`Failed: ${(e as Error).message}`);
      window.setTimeout(() => setCaptureNote(null), 4000);
    } finally {
      setInsertingCapture(false);
    }
  };

  return (
    <div className="pane body-pane">
      <div className="body-toolbar">
        <button
          type="button"
          className="body-toolbar-button"
          onClick={insertCaptureUrl}
          disabled={insertingCapture}
          title="Insert a /c/<id> capture URL at the cursor. Creates a new capture if none exists."
        >
          {insertingCapture ? 'Inserting…' : 'Insert capture URL'}
        </button>
        <div className="body-toolbar-snippet">
          <button
            type="button"
            className="body-toolbar-button"
            onClick={() => setSnippetMenuOpen((v) => !v)}
            aria-expanded={snippetMenuOpen}
            title="Insert a saved share's URL at the cursor"
          >
            Insert share URL ▾
          </button>
          <SharesMenu
            open={snippetMenuOpen}
            onClose={() => setSnippetMenuOpen(false)}
            mode="insert"
            onPick={(_share, url) => {
              insertAtCursor(url);
              setSnippetMenuOpen(false);
            }}
          />
        </div>
        {captureNote && <span className="body-toolbar-note">{captureNote}</span>}
      </div>
      <Editor
        language={editorLanguage}
        value={editorContent}
        onChange={(v) => setEditorContent(v ?? '')}
        onMount={handleMount}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          wordWrap: 'on',
          automaticLayout: true,
          scrollBeyondLastLine: false,
        }}
      />
    </div>
  );
}
